import React, { useState, useEffect } from 'react';
import { pickQuestionnaireLogoUrl } from '../utils/questionnaireBranding';
import styled, { keyframes } from 'styled-components';

// ===== ANIMATIONS =====
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

// ===== STYLED COMPONENTS =====
const WelcomeContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: var(--zyvgo-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  animation: ${fadeIn} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  svg, img {
    max-height: 48px;
  }
`;

const CompanyName = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--zyvgo-text-primary, #222222);
  font-family: var(--zyvgo-font-family);
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
  animation: ${slideUp} 0.6s ease-out 0.1s both;
`;

const StarRating = styled.div`
  display: flex;
  gap: 4px;
  color: #10B981;
  font-size: 20px;
`;

const RatingText = styled.span`
  color: var(--zyvgo-text-primary, #222222);
  font-weight: 600;
  font-size: 1rem;
`;

const ReviewCount = styled.span`
  color: var(--zyvgo-text-secondary, #666666);
  font-size: 0.9375rem;
`;

const Headline = styled.h1`
  text-align: center;
  font-size: 2rem;
  line-height: 1.3;
  margin: 0 0 16px 0;
  color: var(--zyvgo-text-primary, #222222);
  font-weight: 700;
  animation: ${slideUp} 0.6s ease-out 0.2s both;
  
  .highlight {
    color: #DC2626;
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subheadline = styled.p`
  text-align: center;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--zyvgo-text-secondary, #666666);
  margin: 0 0 32px 0;
  animation: ${slideUp} 0.6s ease-out 0.3s both;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const CTAButton = styled.button`
  width: 100%;
  background: var(--zyvgo-primary, #222222);
  color: #FFFFFF;
  border: none;
  padding: 18px 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--zyvgo-font-family);
  transition: all 0.2s ease;
  margin-bottom: 12px;
  animation: ${slideUp} 0.6s ease-out 0.4s both;
  
  &:hover {
    background: #000000;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DisclaimerText = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: var(--zyvgo-text-secondary, #666666);
  margin: 0 0 32px 0;
  animation: ${slideUp} 0.6s ease-out 0.5s both;
`;

const StatsSection = styled.div`
  background: #F0FDF4;
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 16px 20px;
  margin-bottom: 32px;
  text-align: center;
  animation: ${slideUp} 0.6s ease-out 0.6s both;
`;

const StatsText = styled.p`
  color: #059669;
  font-weight: 600;
  font-size: 0.9375rem;
  margin: 0;
`;

const ResultsSection = styled.div`
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 0.7s both;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--zyvgo-text-primary, #222222);
  margin: 0 0 20px 0;
`;

const ResultCard = styled.div`
  background: #FFFFFF;
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 16px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultImages = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
`;

const ResultImage = styled.div`
  position: relative;
  border-radius: var(--zyvgo-radius-sm, 6px);
  overflow: hidden;
  aspect-ratio: 3/4;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: '${props => props.label}';
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }
`;

const ResultInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ResultName = styled.div`
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
  font-size: 0.9375rem;
`;

const ResultWeight = styled.div`
  background: #F0FDF4;
  color: #059669;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
`;

const InfoBox = styled.div`
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 16px;
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 0.8s both;
`;

const InfoText = styled.p`
  color: #92400E;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

const BenefitsSection = styled.div`
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 0.9s both;
`;

const StatsCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: #FFFFFF;
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 20px 16px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--zyvgo-text-primary, #222222);
  margin-bottom: 8px;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: var(--zyvgo-text-secondary, #666666);
  line-height: 1.4;
`;

const ProcessSection = styled.div`
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 1s both;
`;

const ProcessList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProcessItem = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const ProcessIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.25rem;
`;

const ProcessContent = styled.div`
  flex: 1;
`;

const ProcessTitle = styled.div`
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
  margin-bottom: 4px;
  font-size: 0.9375rem;
`;

const ProcessDescription = styled.div`
  color: var(--zyvgo-text-secondary, #666666);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const WeightLossSection = styled.div`
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 1.1s both;
`;

const WeightComparisonCard = styled.div`
  background: #FFFFFF;
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 24px;
  margin-bottom: 20px;
`;

const WeightComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  text-align: center;
`;

const WeightColumn = styled.div``;

const WeightLabel = styled.div`
  font-size: 0.875rem;
  color: var(--zyvgo-text-secondary, #666666);
  margin-bottom: 8px;
  font-weight: 500;
`;

const WeightValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--zyvgo-text-primary, #222222);
  line-height: 1;
`;

const DidYouKnowBox = styled.div`
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 16px;
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 1.2s both;
`;

const DidYouKnowText = styled.p`
  color: #1E40AF;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
  
  strong {
    font-weight: 600;
  }
`;

const TestimonialsSection = styled.div`
  margin-bottom: 32px;
  animation: ${slideUp} 0.6s ease-out 1.3s both;
`;

const TestimonialCard = styled.div`
  background: #FFFFFF;
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: 16px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TestimonialHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TestimonialStars = styled.div`
  color: #FCD34D;
  font-size: 14px;
  display: flex;
  gap: 2px;
`;

const TestimonialTime = styled.div`
  font-size: 0.75rem;
  color: var(--zyvgo-text-secondary, #666666);
`;

const TestimonialText = styled.p`
  color: var(--zyvgo-text-primary, #222222);
  font-size: 0.9375rem;
  margin: 0 0 8px 0;
  line-height: 1.5;
`;

const TestimonialAuthor = styled.div`
  font-size: 0.8125rem;
  color: var(--zyvgo-text-secondary, #666666);
  font-style: italic;
`;

const BottomCTA = styled.div`
  text-align: center;
  animation: ${slideUp} 0.6s ease-out 1.4s both;
`;

// ===== MAIN COMPONENT =====
const WelcomePage = ({ onStart, clientConfig }) => {
  const qc = clientConfig?.questionnaire_config;
  const rawLogo = pickQuestionnaireLogoUrl(qc);
  let logoUrl =
    typeof rawLogo === "string" ? rawLogo.trim() : "";
  if (logoUrl && logoUrl.includes('localstack')) {
    logoUrl = logoUrl.replace('localstack', 'localhost');
  }
  const companyName = qc?.company_name || clientConfig?.name || 'Zÿvgo';
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    setLogoFailed(false);
  }, [rawLogo]);

  return (
    <WelcomeContainer>
      {/* Logo / Company Name */}
      <LogoContainer>
        {logoUrl && !logoFailed ? (
          <img
            src={logoUrl}
            alt={companyName}
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <CompanyName>{companyName}</CompanyName>
        )}
      </LogoContainer>

      {/* Rating */}
      <RatingSection>
        <StarRating>
          <span>★</span>
          <span>★</span>
          <span>★</span>
          <span>★</span>
          <span>★</span>
        </StarRating>
        <RatingText>4.9</RatingText>
        <ReviewCount>(3.2k+ reviews)</ReviewCount>
      </RatingSection>

      {/* Headline */}
      <Headline>
        A Slow Metabolism is <span className="highlight">not</span> your choice.
      </Headline>
      <Subheadline>
        GLP-1 Medication will fix your metabolism so your body burns fat quickly.
      </Subheadline>

      {/* CTA Button */}
      <CTAButton onClick={onStart}>
        See If You Qualify Now →
      </CTAButton>
      <DisclaimerText>
        Free assessment - No credit card required
      </DisclaimerText>

      {/* Stats */}
      <StatsSection>
        <StatsText>
          96% of qualified patients report significant weight loss results
        </StatsText>
      </StatsSection>

      {/* Real Patient Results */}
      <ResultsSection>
        <SectionTitle>Real Patient Results</SectionTitle>
        
        <ResultCard>
          <ResultImages>
            <ResultImage label="Before">
              <img src="/image1.jpg" alt="Before" />
            </ResultImage>
            <ResultImage label="After">
              <img src="/image1.jpg" alt="After" />
            </ResultImage>
          </ResultImages>
          <ResultInfo>
            <ResultName>Albert J., 37</ResultName>
            <ResultWeight>-42lbs</ResultWeight>
          </ResultInfo>
        </ResultCard>

        <ResultCard>
          <ResultImages>
            <ResultImage label="Before">
              <img src="/image2.jpg" alt="Before" />
            </ResultImage>
            <ResultImage label="After">
              <img src="/image2.jpg" alt="After" />
            </ResultImage>
          </ResultImages>
          <ResultInfo>
            <ResultName>Michael T., 45</ResultName>
            <ResultWeight>-38lbs</ResultWeight>
          </ResultInfo>
        </ResultCard>

        <ResultCard>
          <ResultImages>
            <ResultImage label="Before">
              <img src="/image3.jpg" alt="Before" />
            </ResultImage>
            <ResultImage label="After">
              <img src="/image3.jpg" alt="After" />
            </ResultImage>
          </ResultImages>
          <ResultInfo>
            <ResultName>John L., 32</ResultName>
            <ResultWeight>-35lbs</ResultWeight>
          </ResultInfo>
        </ResultCard>
      </ResultsSection>

      {/* Info Box */}
      <InfoBox>
        <InfoText>
          ⚠️ GLP-1 helps you lose weight without surgery by actually fixing your metabolism.
        </InfoText>
      </InfoBox>

      {/* Stats Cards - Inside Cards */}
      <BenefitsSection>
        <SectionTitle>How GLP-1 Treatment Works</SectionTitle>
        <StatsCardsGrid>
          <StatCard>
            <StatNumber>35,000+</StatNumber>
            <StatLabel>Patients Treated</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>15-20%</StatNumber>
            <StatLabel>Avg. Weight Loss</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>98%</StatNumber>
            <StatLabel>Satisfaction Rate</StatLabel>
          </StatCard>
        </StatsCardsGrid>
      </BenefitsSection>

      {/* Process - How It Works */}
      <ProcessSection>
        <SectionTitle>How GLP-1 Treatment Works</SectionTitle>
        <ProcessList>
          <ProcessItem>
            <ProcessIcon>📋</ProcessIcon>
            <ProcessContent>
              <ProcessTitle>Quick & Simple Process</ProcessTitle>
              <ProcessDescription>
                Complete your assessment in just 2 minutes
              </ProcessDescription>
            </ProcessContent>
          </ProcessItem>

          <ProcessItem>
            <ProcessIcon>📉</ProcessIcon>
            <ProcessContent>
              <ProcessTitle>Significant Weight Loss</ProcessTitle>
              <ProcessDescription>
                15-20% average weight loss in first year
              </ProcessDescription>
            </ProcessContent>
          </ProcessItem>

          <ProcessItem>
            <ProcessIcon>📦</ProcessIcon>
            <ProcessContent>
              <ProcessTitle>Discreet Home Delivery</ProcessTitle>
              <ProcessDescription>
                Private shipping directly to your door
              </ProcessDescription>
            </ProcessContent>
          </ProcessItem>

          <ProcessItem>
            <ProcessIcon>💚</ProcessIcon>
            <ProcessContent>
              <ProcessTitle>Average Weight Loss</ProcessTitle>
              <ProcessDescription>
                Our members report an average of 15-20% weight reduction in the first year
              </ProcessDescription>
            </ProcessContent>
          </ProcessItem>
        </ProcessList>
      </ProcessSection>

      {/* Weight Loss Comparison */}
      <WeightLossSection>
        <WeightComparisonCard>
          <WeightComparisonGrid>
            <WeightColumn>
              <WeightLabel>Starting Weight</WeightLabel>
              <WeightValue>200</WeightValue>
            </WeightColumn>
            <WeightColumn>
              <WeightLabel>After Treatment</WeightLabel>
              <WeightValue>160</WeightValue>
            </WeightColumn>
          </WeightComparisonGrid>
        </WeightComparisonCard>
      </WeightLossSection>

      {/* Did You Know Box */}
      <DidYouKnowBox>
        <DidYouKnowText>
          <strong>Did you know?</strong> Unlike diets that leave you hungry, GLP-1 medication resets your body's natural hunger signals, making weight loss easier and sustainable.
        </DidYouKnowText>
      </DidYouKnowBox>

      {/* Verified Patient Success Stories */}
      <TestimonialsSection>
        <SectionTitle>Verified Patient Success Stories</SectionTitle>
        
        <TestimonialCard>
          <TestimonialHeader>
            <TestimonialStars>
              <span>★</span>
              <span>★</span>
              <span>★</span>
              <span>★</span>
              <span>★</span>
            </TestimonialStars>
            <TestimonialTime>2 days ago</TestimonialTime>
          </TestimonialHeader>
          <TestimonialText>
            I've lost 42 pounds in just 4 months. This program changed my life!
          </TestimonialText>
          <TestimonialAuthor>— Maria L., Verified Patient</TestimonialAuthor>
        </TestimonialCard>

        <TestimonialCard>
          <TestimonialHeader>
            <TestimonialStars>
              <span>★</span>
              <span>★</span>
              <span>★</span>
              <span>★</span>
              <span>★</span>
            </TestimonialStars>
            <TestimonialTime>1 week ago</TestimonialTime>
          </TestimonialHeader>
          <TestimonialText>
            After trying everything else, this finally worked for me. Down 35 pounds!
          </TestimonialText>
          <TestimonialAuthor>— James W., Verified Patient</TestimonialAuthor>
        </TestimonialCard>

        <TestimonialCard>
          <TestimonialHeader>
            <TestimonialStars>
              <span>★</span>
              <span>★</span>
              <span>★</span>
              <span>★</span>
              <span>★</span>
            </TestimonialStars>
            <TestimonialTime>3 days ago</TestimonialTime>
          </TestimonialHeader>
          <TestimonialText>
            My metabolism is finally working for me! Already down 22 pounds!
          </TestimonialText>
          <TestimonialAuthor>— Sophia K., Verified Patient</TestimonialAuthor>
        </TestimonialCard>
      </TestimonialsSection>

      {/* Bottom CTA */}
      <BottomCTA>
        <CTAButton onClick={onStart}>
          Begin Your Weight Loss Journey
        </CTAButton>
      </BottomCTA>
    </WelcomeContainer>
  );
};

export default WelcomePage;
